import type { ReactNode } from "react";
import { tv } from "tailwind-variants";

import type { CareplanOrderLine } from "@/domains/careplan/factories/create-careplan-order-model";
import { FREE_SHIPPING_OPTION } from "@/domains/careplan/models/constants";
import type { CareplanOrderModel } from "@/domains/careplan/models/types";
import { CareplanCartOrderLine } from "./careplan-cart-order-line";
import { CareplanCartWhatHappensNext } from "./careplan-cart-what-happens-next";
import { SurveyDiscountBanner } from "./survey-discount-banner";

const careplanCartOrderSummaryVariants = tv({
  slots: {
    root: "flex flex-1 flex-col overflow-y-auto px-4 pt-3 pb-5 sm:px-6 sm:pt-4 sm:pb-6",
    ledger: "flex flex-col gap-2 pt-3.5 text-base text-content",
    row: "flex items-baseline justify-between gap-4",
    rowNote: "text-base text-content-subtle",
    total: "flex items-baseline justify-between gap-4 border-t border-line pt-2",
    totalLabel: "text-xl font-bold",
    totalSuffix: "ml-1.5 text-base font-normal text-content-secondary",
    notes: "mt-3 flex flex-col border-t border-line pt-3 text-base text-content-secondary",
  },
});

const { ledger, notes, root, row, rowNote, total, totalLabel, totalSuffix } =
  careplanCartOrderSummaryVariants();

type LedgerRowProps = {
  label: ReactNode;
  value: ReactNode;
  className?: string;
};

const LedgerRow = ({ className, label, value }: LedgerRowProps) => {
  return (
    <div className={row({ className })}>
      <span>{label}</span>
      <span>{value}</span>
    </div>
  );
};

type CareplanCartOrderSummaryProps = {
  order: NonNullable<CareplanOrderModel>;
  disabled: boolean;
  onQuantityChange: (line: CareplanOrderLine, quantity: number) => void;
  onRestore: (line: CareplanOrderLine) => void;
  showSurveyBanner: boolean;
};

export const CareplanCartOrderSummary = ({
  disabled,
  onQuantityChange,
  onRestore,
  order,
  showSurveyBanner,
}: CareplanCartOrderSummaryProps) => {
  const isShippingFree = !order.shipping || order.shipping.costAsString === FREE_SHIPPING_OPTION;

  return (
    <div className={root()}>
      <ul>
        {order.lines.map((line) => {
          return (
            <CareplanCartOrderLine
              disabled={disabled}
              key={line.variantId ?? line.id}
              line={line}
              onQuantityChange={(quantity) => {
                return onQuantityChange(line, quantity);
              }}
              onRestore={() => {
                return onRestore(line);
              }}
            />
          );
        })}
      </ul>

      <div className={ledger()}>
        <LedgerRow label="Subtotal" value={`$${order.subtotalAsString}`} />

        <div>
          <LedgerRow
            className="font-semibold text-danger"
            label="Care plan savings"
            value={`- $${order.savingsAsString}`}
          />
          <p className={rowNote()}>
            {order.hasCareplanCode
              ? "$5 off each product + 10% for subscribing"
              : "10% for subscribing"}
          </p>
        </div>

        {order.extraDiscounts.map((discount) => {
          return (
            <LedgerRow
              className="font-semibold text-danger"
              key={discount.code}
              label={discount.code}
              value={`- $${discount.displayTotal}`}
            />
          );
        })}

        {order.shipping ? (
          <LedgerRow
            label={isShippingFree ? "Shipping" : order.shipping.title}
            value={isShippingFree ? FREE_SHIPPING_OPTION : `$${order.shipping.costAsString}`}
          />
        ) : null}

        <div className={total()}>
          <span>
            <span className={totalLabel()}>Total</span>
            <span className={totalSuffix()}>per month</span>
          </span>
          <span className={totalLabel()}>${order.totalAsString}</span>
        </div>
      </div>

      <div className={notes()}>
        {order.isEmpty ? null : (
          <p>{`You'll be charged $${order.totalAsString} today and every 30 days after.`}</p>
        )}
        <p>Change products or cancel anytime.</p>
      </div>

      {showSurveyBanner ? (
        <div className="mt-4">
          <SurveyDiscountBanner />
        </div>
      ) : null}

      <div className="mt-5">
        <CareplanCartWhatHappensNext />
      </div>
    </div>
  );
};
